/**
 * The pages as text, written into the HTML before the app arrives.
 *
 * The app draws everything with script, so the HTML a crawler downloads used
 * to hold a menu and nothing else: seventy-odd words, none of them about the
 * venue the address names. A search engine may run the script later, or may
 * not bother for a thousand near-identical shells. So the Worker writes the
 * same content the app is about to show into the same places the app will
 * show it — the venue card, the programme screen — using the app's own
 * classes. The app then replaces it with the live version, which says the same
 * thing. Nothing here is for crawlers only: a visitor sees it too, earlier
 * than before, and a page that shows a crawler something different from what
 * it shows people is a page a search engine penalises.
 *
 * Everything that comes from the catalogue goes through `esc`: names are hand
 * typed, and a name is a name, not markup.
 */

import {
  episodeName,
  episodeTitleAndTown,
  episodeSubtitle,
  episodeTownToAdd,
  episodeCounts,
  episodesByMonth,
  episodesPath,
  FOLLOW,
  venueEpisodeKey,
  winnersFirst,
} from '../src/puntate.js';
import {
  GUIDE_ID,
  GUIDE_OFFICIAL,
  GUIDE_SOURCE,
  GUIDE_YEAR,
  byStars,
  guideHead,
  guideVenueHead,
  isGuide,
  guideByCity,
  guideCitySlug,
  guideCityTitle,
  starsGlyph,
  starsSummary,
  starsWords,
  tvChef,
} from '../src/guida.js';
import { STATUS_PROGRAM, isGone, statusIntro } from '../src/chiusi.js';
import { episodeStoryHtml, inPlace } from '../src/racconti.js';

/** A zone gets a page of its own from this many venues up. */
export const MIN_ZONE = 4;

/*
 * Zones are named without a preposition on purpose. Many are not towns —
 * "Lago di Garda", "Valtellina", "Puglia", "Cinque Terre" — and "a Lago di
 * Garda" is wrong where "a Genova" is right. A list of the right preposition
 * for every zone would need keeping up with every zone added; the neutral
 * form is right for all of them. The app does the same.
 */

/** "Tutti i 17", "tutti gli 8": the article follows how the number is said. */
export function allOf(n) {
  return /^8/.test(String(n)) || n === 11 ? `Tutti gli ${n}` : `Tutti i ${n}`;
}

/**
 * The address of a zone: 'Lago di Garda' is `lago-di-garda`, 'Forlì' is
 * `forli`. The app has the same function in `src/format.js`; the two must
 * agree, or a link written here opens nothing there.
 */
export function zoneSlug(zona) {
  return String(zona ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function esc(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const MONTHS = [
  'gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno',
  'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre',
];

/** As `formatDate` in the app: '2026-09-08' is '8 settembre 2026'. */
export function italianDate(iso, precision = 'giorno') {
  if (!iso) return null;
  const [year, month, day] = iso.split('-');
  if (!month) return year;
  const monthName = MONTHS[Number(month) - 1] ?? month;
  if (precision === 'giorno' && day) return `${Number(day)} ${monthName} ${year}`;
  return `${monthName} ${year}`;
}

/** The same words the app's card uses, so the two versions read alike. */
export const STATUS_LABELS = {
  aperto: 'Aperto',
  chiuso: 'Chiuso',
  chiuso_temporaneamente: 'Chiuso temporaneamente',
  ignoto: 'Stato non verificato',
};

/** As a sentence, for titles and descriptions. */
const STATUS_SENTENCE = {
  aperto: 'Risulta aperto',
  chiuso: 'Risulta chiuso',
  chiuso_temporaneamente: 'Risulta chiuso temporaneamente',
  ignoto: 'Stato da verificare',
};

export function outcomeLabel(venue) {
  if (venue.vincitore) return 'Vincitore della puntata';
  if (venue.posizione) return `${venue.posizione}º classificato`;
  return null;
}

/**
 * Who presents the programme, where nobody could doubt it.
 *
 * People search "4 ristoranti Borghese" as often as the title alone. Only
 * names that are certain go here: a wrong presenter on a page is worse than
 * none. Camionisti in Trattoria is left out because it has had two. The app
 * shows the same name with its own copy in `src/format.js`; the two must
 * agree.
 */
const PROGRAM_HOSTS = {
  '4-ristoranti': 'Alessandro Borghese',
  'cucine-da-incubo': 'Antonino Cannavacciuolo',
  foodish: 'Joe Bastianich',
  'food-advisor': 'Simone Rugiati',
};

/** Who presents a programme, or nothing where it is not certain. */
export function programHost(programId) {
  return PROGRAM_HOSTS[programId] ?? null;
}

export function programLongName(programId, programName) {
  if (programId === GUIDE_ID) return `Ristoranti stellati Michelin ${GUIDE_YEAR}`;
  const host = PROGRAM_HOSTS[programId];
  return host ? `${programName} di ${host}` : programName;
}

/* ---------- regions ---------- */

/**
 * Province code to region, for grouping a programme's venues the way people
 * think of the map. The catalogue has no region column; the province code
 * closes nearly every confirmed address ("16123 Genova GE").
 */
const REGION_OF_PROVINCE = (() => {
  const regions = {
    Abruzzo: 'AQ CH PE TE',
    Basilicata: 'MT PZ',
    Calabria: 'CS CZ KR RC VV',
    Campania: 'AV BN CE NA SA',
    'Emilia-Romagna': 'BO FC FE MO PC PR RA RE RN',
    'Friuli-Venezia Giulia': 'GO PN TS UD',
    Lazio: 'FR LT RI RM VT',
    Liguria: 'GE IM SP SV',
    Lombardia: 'BG BS CO CR LC LO MB MI MN PV SO VA',
    Marche: 'AN AP FM MC PU',
    Molise: 'CB IS',
    Piemonte: 'AL AT BI CN NO TO VB VC',
    Puglia: 'BA BR BT FG LE TA',
    Sardegna: 'CA NU OR SS SU',
    Sicilia: 'AG CL CT EN ME PA RG SR TP',
    Toscana: 'AR FI GR LI LU MS PI PO PT SI',
    'Trentino-Alto Adige': 'BZ TN',
    Umbria: 'PG TR',
    "Valle d'Aosta": 'AO',
    Veneto: 'BL PD RO TV VE VI VR',
  };
  const map = new Map();
  for (const [region, codes] of Object.entries(regions)) {
    for (const code of codes.split(' ')) map.set(code, region);
  }
  return map;
})();

function regionFromAddress(address) {
  const code = /\b([A-Z]{2})\s*$/.exec(String(address ?? '').trim())?.[1];
  return code ? REGION_OF_PROVINCE.get(code) ?? null : null;
}

/**
 * Each zone's region, by majority of the addresses in it.
 *
 * Per zone and not per venue, so that a zone is never split across two
 * headings, and a venue with no address still lands where its neighbours are.
 */
function regionsByZone(venues) {
  const votes = new Map();
  for (const venue of venues) {
    const region = regionFromAddress(venue.indirizzo);
    if (!region || !venue.zona) continue;
    const tally = votes.get(venue.zona) ?? new Map();
    tally.set(region, (tally.get(region) ?? 0) + 1);
    votes.set(venue.zona, tally);
  }
  const result = new Map();
  for (const [zona, tally] of votes) {
    result.set(zona, [...tally].sort((a, b) => b[1] - a[1])[0][0]);
  }
  return result;
}

/* ---------- pieces ---------- */

function kv(term, value) {
  return `<div class="kv"><dt>${esc(term)}</dt><dd>${esc(value)}</dd></div>`;
}

/** One venue as a line in a list: its link, and what the card would add. */
function venueItem(venue, { zone = false } = {}) {
  const notes = [];
  if (zone && venue.zona) notes.push(esc(venue.zona));
  if (venue.vincitore) notes.push('vincitore');
  if (venue.stato && venue.stato !== 'aperto') notes.push(esc(STATUS_LABELS[venue.stato] ?? '').toLowerCase());
  const tail = notes.length ? ` <small>· ${notes.join(' · ')}</small>` : '';
  return `<li><a href="/locale/${esc(venue.id)}">${esc(venue.nome ?? venue.id)}</a>${tail}</li>`;
}

function episodeKey(venue) {
  return `${venue.puntata?.data ?? ''}|${venue.puntata?.titolo ?? ''}`;
}

/** Venues grouped by episode, most recent first, winners first within each. */
function byEpisode(venues) {
  const groups = new Map();
  for (const venue of venues) {
    const key = episodeKey(venue);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(venue);
  }
  return [...groups.values()]
    .map((list) => list.sort(winnersFirst))
    .sort((a, b) => (b[0].puntata?.data ?? '').localeCompare(a[0].puntata?.data ?? ''));
}

/** An episode's heading in a list, linked to its page where it has one. */
function episodeHeading(venue, episodes) {
  const title = venue.puntata?.titolo;
  const date = italianDate(venue.puntata?.data, venue.puntata?.precisione);
  const page = episodes?.byKey.get(venueEpisodeKey(venue));
  const named = page && title ? `<a href="/puntata/${esc(page.slug)}">${esc(title)}</a>` : esc(title ?? '');
  if (title && date) return `${named} <small>· ${esc(date)}</small>`;
  return named || esc(date ?? 'Puntata non identificata');
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/* ---------- the venue ---------- */

/**
 * Title and description of a venue's page: the questions people actually
 * type — which programme, where, is it still open — answered before the click.
 */
export function venueHead(venue, programName) {
  if (isGuide(venue)) {
    const checked = italianDate(venue.verificato_il);
    return guideVenueHead(
      venue,
      venue.stato && venue.stato !== 'ignoto'
        ? `${STATUS_SENTENCE[venue.stato]}${checked ? `, verificato il ${checked}` : ''}.`
        : null
    );
  }
  if (venue.programma === STATUS_PROGRAM) return statusVenueHead(venue, programName);
  const where = venue.zona ? `, ${venue.zona}` : '';
  const title = venue.vincitore
    ? `${venue.nome}${where}: il vincitore di ${programName}`
    : `${venue.nome}${where}: visto in ${programName}`;

  const sentences = [];
  const episode = venue.puntata?.titolo ? ` "${venue.puntata.titolo}"` : '';
  const date = italianDate(venue.puntata?.data, venue.puntata?.precisione);
  const when = date ? ` (${date})` : '';
  sentences.push(
    venue.vincitore
      ? `Ha vinto la puntata di ${programName}${episode}${when}.`
      : `Apparso nella puntata di ${programName}${episode}${when}.`
  );
  const checked = italianDate(venue.verificato_il);
  sentences.push(
    `${STATUS_SENTENCE[venue.stato] ?? STATUS_SENTENCE.ignoto}${
      checked && venue.stato !== 'ignoto' ? `, verificato il ${checked}` : ''
    }.`
  );
  if (venue.indirizzo && venue.indirizzo_confermato) sentences.push(`${venue.indirizzo}.`);
  else if (venue.zona) sentences.push(`Zona: ${venue.zona}.`);

  return { title, description: sentences.join(' ') };
}

/**
 * A Cucine da Incubo venue, under the question people ask about it: "che fine
 * ha fatto" the restaurant after Cannavacciuolo's visit. The answer opens the
 * description, from the same groups as `/cucine-da-incubo-chiusi-e-aperti`.
 */
function statusVenueHead(venue, programName) {
  const name = venue.nome ?? venue.id;
  const checked = italianDate(venue.verificato_il);
  let answer;
  if (venue.stato && venue.stato !== 'ignoto') {
    answer = `${STATUS_SENTENCE[venue.stato]}${checked ? `, verificato il ${checked}` : ''}.`;
  } else if (isGone(venue)) {
    answer = 'Non risulta più attivo, non lo troviamo né su Google Maps né altrove.';
  } else {
    answer = 'Non abbiamo notizie sicure.';
  }
  const lead = `${name}${venue.zona ? ` (${venue.zona})` : ''} dopo ${programName}: `;
  const sentences = [lead + answer.charAt(0).toLowerCase() + answer.slice(1)];
  const date = italianDate(venue.puntata?.data, venue.puntata?.precisione);
  if (date) sentences.push(`Apparso nella puntata ${/^\d/.test(date) ? ofDate(date) : `di ${date}`}.`);
  if (venue.indirizzo && venue.indirizzo_confermato) sentences.push(`${venue.indirizzo}.`);
  return {
    // The question first, in the words people search it with: a long name
    // or town is cut off at the end, where it matters least.
    title: `Che fine ha fatto ${name} dopo ${programName}?${venue.zona ? ` · ${venue.zona}` : ''}`,
    description: sentences.join(' '),
  };
}

/**
 * The venue card, as `renderDetail` in `src/detail-view.js` draws it, minus
 * the buttons: they need the script, and a button that does nothing is worse
 * than none for the second or two before the app takes over.
 */
export function venueBody(
  venue,
  { programName, programNames, others, twins = [], neighbours, zoneCount, episode }
) {
  const parts = [];

  const hero = [`<h1>${esc(venue.nome ?? venue.id)}</h1>`];
  if (venue.nome_in_onda && venue.nome_in_onda !== venue.nome) {
    hero.push(`<p class="hero__former">In onda come ${esc(venue.nome_in_onda)}</p>`);
  }
  if (venue.zona) hero.push(`<p>${esc(venue.zona)}</p>`);
  const outcome = outcomeLabel(venue);
  hero.push(
    `<div class="card__meta"><a class="prog" href="/programma/${esc(venue.programma)}">` +
      `<span>${esc(programName)}</span></a>` +
      (outcome ? `<span class="tag tag--win">${esc(outcome)}</span>` : '') +
      `<span class="state state--${esc(venue.stato)}">${esc(STATUS_LABELS[venue.stato] ?? '')}</span></div>`
  );
  parts.push(`<div class="hero">${hero.join('')}</div>`);

  // This venue's own, before the places around it, as in the app.
  const today = [
    `<div class="kv"><dt>Stato</dt><dd class="state state--${esc(venue.stato)}">${esc(
      STATUS_LABELS[venue.stato] ?? ''
    )}</dd></div>`,
  ];
  const checked = italianDate(venue.verificato_il);
  if (checked) today.push(kv('Verificato', checked));
  if (venue.indirizzo) today.push(kv('Indirizzo', venue.indirizzo));
  if (venue.telefono) today.push(kv('Telefono', venue.telefono));
  parts.push(
    `<div class="block"><p class="block__label">Il locale oggi</p><dl class="kv-list kv-list--spaced">${today.join('')}</dl></div>`
  );

  const episodePage = episode;
  const rows = [kv('Programma', programName)];
  if (venue.puntata?.titolo) rows.push(kv('Puntata', venue.puntata.titolo));
  const aired = italianDate(venue.puntata?.data, venue.puntata?.precisione);
  if (aired) rows.push(kv('Andata in onda', aired));
  const placing =
    outcome ?? (others.some((other) => other.vincitore) ? 'Non ha vinto' : null);
  if (placing) rows.push(kv('Piazzamento', placing));
  const toEpisode = episodePage
    ? `<p class="links__all"><a href="/puntata/${esc(episodePage.slug)}">Vedi la puntata →</a></p>`
    : '';
  parts.push(
    `<div class="block"><p class="block__label">Puntata</p><dl class="kv-list">${rows.join('')}</dl>${toEpisode}</div>`
  );

  if (others.length) {
    parts.push(
      `<div class="block"><h2 class="block__label">${
        others.length === 1 ? 'L’altro della puntata' : 'Gli altri della puntata'
      }</h2><ul class="links">${[...others].sort(winnersFirst).map((o) => venueItem(o)).join('')}</ul></div>`
    );
  }

  // The same place in another programme (`twinsBlock` in the app).
  if (twins.length) {
    parts.push(
      `<div class="block"><h2 class="block__label">${
        twins.length === 1 ? 'Lo stesso locale, in un’altra puntata' : 'Lo stesso locale, in altre puntate'
      }</h2><ul class="links">${twins
        .map((twin) => {
          const notes = [
            programNames?.get(twin.programma) ?? twin.programma,
            twin.puntata?.titolo,
            italianDate(twin.puntata?.data, twin.puntata?.precisione),
            twin.vincitore ? 'vincitore' : null,
          ].filter(Boolean);
          return `<li><a href="/locale/${esc(twin.id)}">${esc(twin.nome ?? twin.id)}</a> <small>· ${notes
            .map(esc)
            .join(' · ')}</small></li>`;
        })
        .join('')}</ul></div>`
    );
  }

  if (venue.zona && neighbours.length) {
    const all =
      zoneCount >= MIN_ZONE
        ? `<p class="links__all"><a href="/zona/${esc(zoneSlug(venue.zona))}">${allOf(zoneCount)} locali della zona</a></p>`
        : '';
    parts.push(
      `<div class="block"><h2 class="block__label">Altri locali visti in TV · ${esc(
        venue.zona
      )}</h2><ul class="links">${neighbours.map((n) => venueItem(n)).join('')}</ul>${all}</div>`
    );
  }

  // Whichever block ends the card keeps the room the bottom bar covers, as in
  // the app.
  parts[parts.length - 1] = parts[parts.length - 1].replace(
    '<div class="block">',
    '<div class="block block--last">'
  );
  return parts.join('');
}

/* ---------- a programme ---------- */

/** From how many venues a programme's title says how many. */
const COUNT_IN_TITLE = 20;

export function programHead(programId, programName, venues) {
  if (programId === GUIDE_ID) return guideHead(venues);
  const long = programLongName(programId, programName);
  const open = venues.filter((venue) => venue.stato === 'aperto').length;
  const episodes = new Set(venues.map(episodeKey)).size;
  if (!venues.length) {
    return {
      title: `${long}: i locali della trasmissione`,
      description: 'Nessun locale ancora in catalogo per questa trasmissione.',
    };
  }
  // The count says "the whole list" where it is large; on a handful of
  // venues it says the opposite, and is left out.
  return {
    title:
      venues.length >= COUNT_IN_TITLE
        ? `${long}: la mappa dei ${venues.length} locali e dei vincitori`
        : `${long}: la mappa dei locali e dei vincitori`,
    description:
      `Tutti i ristoranti di ${plural(episodes, 'puntata', 'puntate')} di ${programName}, ` +
      `regione per regione: chi ha vinto, dove sono e quali sono ancora aperti (${open} su ${venues.length}).`,
  };
}

/** The home page's head: what the whole site is, in the words people search. */
export const HOME_TITLE = 'Visto e Mangiato: la mappa dei ristoranti visti in TV';
export const HOME_DESCRIPTION =
  '4 Ristoranti, Cucine da Incubo, Foodish e altri programmi: più di mille locali ' +
  'sulla mappa, con chi ha vinto ogni puntata e quali sono ancora aperti.';

/**
 * The programmes as the profile screen lists them, written into the home page.
 *
 * The same rows the app draws (`renderProgramRows` in `src/main.js`), as links
 * to each programme's page: without them the home page linked nowhere, and a
 * search engine could reach the programmes only through the sitemap.
 */
export function homeProgramRows(programs, counts) {
  const row = (program) => {
    const owned = counts.get(program.id) ?? 0;
    const guide = program.id === GUIDE_ID;
    const text =
      `<span><b>${guide ? '★ ' : ''}${esc(program.nome)}</b><small>` +
      (owned
        ? guide
          ? `${plural(owned, 'ristorante', 'ristoranti')}, città per città`
          : `${plural(owned, 'locale', 'locali')} in catalogo`
        : 'nessun locale ancora') +
      '</small></span>';
    return owned
      ? `<a class="row-link" href="/programma/${esc(program.id)}">${text}</a>`
      : `<span class="row-link is-disabled">${text}</span>`;
  };
  // The guide after the programmes, under a heading of its own: it is not one
  // of them, and the profile says so (`renderProgramRows` in `src/main.js`).
  const shows = programs.filter((program) => program.id !== GUIDE_ID).map(row).join('');
  const guides = programs.filter((program) => program.id === GUIDE_ID).map(row).join('');
  return guides ? `${shows}<p class="group-label">Guide</p>${guides}` : shows;
}

export function programSummary(venues) {
  const wins = venues.filter((venue) => venue.vincitore).length;
  const open = venues.filter((venue) => venue.stato === 'aperto').length;
  const parts = [plural(venues.length, 'locale in catalogo', 'locali in catalogo')];
  if (wins) parts.push(plural(wins, 'vincitore di puntata', 'vincitori di puntata'));
  if (venues.length) parts.push(open === 1 ? '1 risulta aperto' : `${open} risultano aperti`);
  return parts.join(' · ');
}

/**
 * A programme's venues by region, then zone, then episode. Zones with a page
 * of their own link to it.
 */
export function programBody(venues, zoneCounts, episodePages) {
  const regionOf = regionsByZone(venues);
  const regions = new Map();
  for (const venue of venues) {
    const region = regionOf.get(venue.zona) ?? 'Altre zone';
    const zone = venue.zona ?? 'Zona non indicata';
    if (!regions.has(region)) regions.set(region, new Map());
    const zones = regions.get(region);
    if (!zones.has(zone)) zones.set(zone, []);
    zones.get(zone).push(venue);
  }

  const order = [...regions.keys()].sort((a, b) => {
    if (a === 'Altre zone') return 1;
    if (b === 'Altre zone') return -1;
    return a.localeCompare(b, 'it');
  });

  return order
    .map((region) => {
      const zones = [...regions.get(region)].sort((a, b) => a[0].localeCompare(b[0], 'it'));
      const body = zones
        .map(([zone, list]) => {
          const name =
            (zoneCounts.get(zone) ?? 0) >= MIN_ZONE
              ? `<a href="/zona/${esc(zoneSlug(zone))}">${esc(zone)}</a>`
              : esc(zone);
          const episodes = byEpisode(list)
            .map(
              (group) =>
                `<p class="links__episode">${episodeHeading(group[0], episodePages)}</p>` +
                `<ul class="links">${group.map((v) => venueItem(v)).join('')}</ul>`
            )
            .join('');
          return `<h4 class="links__zone">${name}</h4>${episodes}`;
        })
        .join('');
      return `<h3 class="group-label">${esc(region)}</h3>${body}`;
    })
    .join('');
}

/* ---------- a zone ---------- */

export function zoneHead(zona, venues, programNames) {
  const open = venues.filter((venue) => venue.stato === 'aperto').length;
  const names = [...new Set(venues.map((venue) => programNames.get(venue.programma)))]
    .filter(Boolean);
  const list =
    names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : names[0] ?? '';
  return {
    title: `${zona}: ${venues.length} ristoranti visti in TV`,
    description:
      `${zona}, i locali passati in ${list}: indirizzi, puntate, vincitori. ` +
      `${open} su ${venues.length} ${open === 1 ? 'risulta ancora aperto' : 'risultano ancora aperti'}.`,
  };
}

export function zoneTitle(zona) {
  return `${zona}: i ristoranti visti in TV`;
}

/** A zone's venues by programme, then episode. */
export function zoneBody(venues, programs, episodePages) {
  const order = programs.map((program) => program.id);
  const groups = new Map();
  for (const venue of venues) {
    if (!groups.has(venue.programma)) groups.set(venue.programma, []);
    groups.get(venue.programma).push(venue);
  }
  return [...groups]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([programId, list]) => {
      const name = programs.find((program) => program.id === programId)?.nome ?? programId;
      const episodes = byEpisode(list)
        .map(
          (group) =>
            `<p class="links__episode">${episodeHeading(group[0], episodePages)}</p>` +
            `<ul class="links">${group.map((v) => venueItem(v)).join('')}</ul>`
        )
        .join('');
      return `<h3 class="group-label"><a href="/programma/${esc(programId)}">${esc(
        name
      )}</a> (${list.length})</h3>${episodes}`;
    })
    .join('');
}

/* ---------- an episode ---------- */

/** "Del 14 ottobre", "dell'8 ottobre", "dell'1 ottobre". */
function ofDate(date) {
  return /^(8|11|1) /.test(date) ? `dell’${date}` : `del ${date}`;
}

function andList(names) {
  return names.length > 1 ? `${names.slice(0, -1).join(', ')} e ${names.at(-1)}` : names[0] ?? '';
}

/**
 * The episode's title and description: programme, dish and town, which is how
 * people search an episode ("foodish avocado toast milano"), and the names of
 * the venues, which is the other half of what they search.
 */
const FOUR_RESTAURANTS = '4-ristoranti';

/**
 * "4 Ristoranti del Canavese" for "Miglior ristorante del Canavese", which is
 * how people search it. Only where a preposition follows: "Miglior ristorante
 * chic di Monza" keeps its words, since "4 Ristoranti chic di Monza" is wrong.
 */
function fourRestaurantsName(name) {
  const rest =
    /^Migliori? ristorante\s+((?:(?:di|del|della|dello|dei|degli|delle|nel|nella|nei|in|sul|sulla|sui|lungo|tra|con|a|al|alla)\s|dell[’']).*)$/i.exec(
      name
    );
  return rest ? `4 Ristoranti ${rest[1]}` : `4 Ristoranti, ${name.charAt(0).toLowerCase()}${name.slice(1)}`;
}

/**
 * Programmes whose episodes are a dish in a town, searched as such and for who
 * won ("foodish valtellina chi ha vinto"): the title says the winner, like
 * 4 Ristoranti's. Camionisti in trattoria is left out: its titles are regions.
 */
const DISH_CONTESTS = new Set(['foodish', 'food-advisor']);

/** "Miglior pad thai" → "miglior pad thai"; a name keeps its capital. */
function dishInSentence(name) {
  return /^(?:(?:il|lo|la|i|gli|le)\s+)?migliori?e?\s/i.test(name)
    ? `${name.charAt(0).toLowerCase()}${name.slice(1)}`
    : name;
}

export function episodeHead(episode, venues, programId, programName) {
  const date = italianDate(episode.data);
  const town = episodeTownToAdd(episode);
  const winner = venues.find((venue) => venue.vincitore);
  const winnerName = winner ? winner.nome ?? winner.id : null;
  const n = venues.length;
  const named = `${episodeName(episode, date)}${town ? ` · ${town}` : ''}`;
  // People search who won, all year: the answer closes the title and opens
  // the description, instead of a title that promises it.
  const winnerFirst = Boolean(winner) && (programId === FOUR_RESTAURANTS || DISH_CONTESTS.has(programId));
  let title;
  if (winner && programId === FOUR_RESTAURANTS) {
    title = `${fourRestaurantsName(named)}: ha vinto ${winnerName}`;
  } else if (winner && DISH_CONTESTS.has(programId) && episode.titolo) {
    title = `${programName}, ${dishInSentence(episode.titolo)}${town ? ` ${inPlace(town)}` : ''}: ha vinto ${winnerName}`;
  } else {
    title =
      `${programName}, ${named}: ` +
      (winner ? `i ${n} locali e chi ha vinto` : `i ${n} locali della puntata`);
  }

  const host = PROGRAM_HOSTS[programId];
  const sentences = [
    `La puntata di ${programName}${date ? ` ${ofDate(date)}` : ''}${host ? `, con ${host}` : ''}: ` +
      `${andList([...venues].sort(winnersFirst).map((venue) => venue.nome ?? venue.id))}.`,
  ];
  if (winnerFirst) sentences.unshift(`Ha vinto ${winnerName}.`);
  else if (winner) sentences.push(`Ha vinto ${winnerName}.`);
  // Left out until somebody has checked: on an episode added tonight, "0 su 4"
  // would read as four closed places rather than four nobody has looked up.
  const open = venues.filter((venue) => venue.stato === 'aperto').length;
  if (venues.some((venue) => venue.stato !== 'ignoto')) {
    sentences.push(
      open === 1 ? `1 su ${n} risulta ancora aperto.` : `${open} su ${n} risultano ancora aperti.`
    );
  }
  return { title, description: sentences.join(' ') };
}

/**
 * "La puntata" and the dish or the place, above the venues (`src/racconti.js`).
 * The texts are passed in rather than imported: the app imports this file, and
 * loads them only when it needs them.
 */
export function episodeStory(episode, venues, programName, racconti) {
  return episodeStoryHtml(
    episode,
    venues,
    { programName, host: PROGRAM_HOSTS[episode.programma] ?? null, date: italianDate(episode.data) },
    racconti
  );
}

/** The line under the title: programme, town, date. */
export function episodeSub(episode, programName) {
  return episodeSubtitle(episode, programName, italianDate(episode.data));
}

export function episodeTitle(episode) {
  return episodeName(episode, italianDate(episode.data));
}

/** One venue of the episode: as the app's card, with the address it shows. */
function episodeVenueItem(venue) {
  const notes = [];
  if (venue.vincitore) notes.push('Vincitore della puntata');
  const where = venue.indirizzo && venue.indirizzo_confermato ? venue.indirizzo : venue.zona;
  if (where) notes.push(esc(where));
  notes.push(esc(STATUS_LABELS[venue.stato] ?? ''));
  return `<li><a href="/locale/${esc(venue.id)}">${esc(venue.nome ?? venue.id)}</a> <small>· ${notes.join(' · ')}</small></li>`;
}

/**
 * The episode's venues, winner first, then the way on: other episodes of the
 * same programme in the same town, and the programme itself. The app draws
 * the same (`openEpisode` in `src/main.js`), with cards for the lines.
 */
export function episodeBody(episode, venues, { programId, programName, siblings }) {
  const parts = [`<ul class="links">${[...venues].sort(winnersFirst).map(episodeVenueItem).join('')}</ul>`, followHtml()];
  if (siblings.length) {
    parts.push(
      `<h3 class="group-label">Altre puntate di ${esc(programName)} · ${esc(episode.citta)}</h3>` +
        `<ul class="links">${siblings
          .map((other) => {
            const date = italianDate(other.data);
            return `<li><a href="/puntata/${esc(other.slug)}">${esc(episodeName(other, date))}</a>${
              date ? ` <small>· ${esc(date)}</small>` : ''
            }</li>`;
          })
          .join('')}</ul>`
    );
  }
  parts.push(
    `<p class="links__all"><a href="${esc(episodesPath(programId))}">Tutte le puntate di ${esc(programName)}</a></p>`
  );
  return parts.join('');
}

/** "Seguici", with the links to Instagram and Facebook. */
function followHtml() {
  return (
    `<div class="follow"><p class="follow__text">${esc(FOLLOW.text)}</p><p class="follow__links">${FOLLOW.links
      .map(([name, url]) => `<a class="follow__link" href="${esc(url)}" target="_blank" rel="noopener">${esc(name)}</a>`)
      .join('')}</p></div>`
  );
}

/* ---------- every episode ---------- */

/**
 * The "Puntate" screen's head: every episode, or one programme's.
 *
 * A programme's leads with its latest episode and who won it: the night after
 * an episode people search "foodish chi ha vinto ieri sera", or "stasera",
 * with no dish in the words, and this is the page that answers whichever
 * episode that was. The date rather than "ieri": the page is kept until the
 * data changes, and "ieri" would be wrong by the next morning.
 */
export function episodesHead(programId, programName, n, latest = null, winner = null) {
  if (programId) {
    const last = latest ? italianDate(latest.data) : null;
    const lastShort = last ? last.replace(/ \d{4}$/, '') : null;
    return {
      title: latest
        ? `${programName}, chi ha vinto: ${episodeTitleAndTown(latest)}` +
          `${lastShort ? ` (${lastShort})` : ''} e tutte le ${n} puntate`
        : `${programLongName(programId, programName)}: tutte le ${n} puntate`,
      description:
        (latest
          ? `L'ultima puntata${last ? `, ${last}` : ''}: ${episodeTitleAndTown(latest)}` +
            `${winner ? `, ha vinto ${winner}` : ''}. `
          : '') +
        `Tutte le ${n} puntate di ${programName}, dalla più recente: per ognuna i locali, ` +
        'chi ha vinto e quali sono ancora aperti.',
    };
  }
  return {
    title: 'Tutte le puntate: i ristoranti visti in TV, puntata per puntata',
    description:
      `Le ${n} puntate di 4 Ristoranti, Foodish e degli altri programmi, dalla più recente: ` +
      'per ognuna i locali, chi ha vinto e quali sono ancora aperti.',
  };
}

/** The line under the screen's title. */
export function episodesSub(n) {
  return `${plural(n, 'puntata', 'puntate')} · dalla più recente`;
}

/**
 * Every episode as a link to its page, by month, with the programmes as
 * chips above. The app draws the same (`renderEpisodesTab` in `src/main.js`).
 */
export function episodesBody(index, programs, programId, winners = new Map()) {
  const counts = episodeCounts(index);
  const chip = (id, name, n) =>
    `<a class="chip" href="${esc(episodesPath(id))}" aria-pressed="${id === programId}">` +
    `<span class="chip__label">${esc(name)}</span> <span class="chip__n">${n}</span></a>`;
  const chips = [chip(null, 'Tutte', index.bySlug.size)];
  for (const program of programs) {
    const n = counts.get(program.id);
    if (n) chips.push(chip(program.id, program.nome, n));
  }
  const names = new Map(programs.map((program) => [program.id, program.nome]));
  const months = episodesByMonth(index, programId)
    .map(
      ({ mese, episodes }) =>
        `<h3 class="group-label">${esc(mese ?? 'Senza data')}</h3>` +
        episodes
          .map((episode) => {
            const notes = [
              programId ? null : names.get(episode.programma) ?? episode.programma,
              italianDate(episode.data),
              plural(episode.n, 'locale', 'locali'),
              winners.has(episode.key) ? `ha vinto ${winners.get(episode.key)}` : null,
            ].filter(Boolean);
            return (
              `<a class="row-link" href="/puntata/${esc(episode.slug)}"><span>` +
              `<b>${esc(episodeTitleAndTown(episode))}</b><small>${esc(notes.join(' · '))}</small>` +
              '</span></a>'
            );
          })
          .join('')
    )
    .join('');
  return `<div class="chips chips--wrap episodes__chips">${chips.join('')}</div>${months}`;
}

/* ---------- the latest episodes ---------- */

/**
 * The latest episodes as links, for the row above the list on the home page.
 * The app draws the same row (`renderRecent` in `src/main.js`), with the way
 * to all of them at its head.
 */
export function recentEpisodesHtml(episodes, programNames) {
  if (!episodes.length) return '';
  return (
    `<div class="recent__head"><p class="block__label">${
      episodes.length === 1 ? 'Ultima puntata' : 'Ultime puntate'
    }</p><a class="recent__all" href="/puntate">Tutte le puntate</a></div><ul class="recent__list">` +
    episodes
      .map(
        (episode) =>
          `<li><a class="recent__link" href="/puntata/${esc(episode.slug)}">` +
          `<span class="recent__title">${esc(episodeTitleAndTown(episode))}</span>` +
          `<span class="recent__prog">${esc(programNames.get(episode.programma) ?? episode.programma)}</span></a></li>`
      )
      .join('') +
    '</ul>'
  );
}

/* ---------- the Michelin guide ---------- */

/** The credit under every list of the guide: the source, and who we are not. */
function guideCredit() {
  return (
    `<p class="note">Dati: <a href="${esc(GUIDE_SOURCE)}" rel="noopener">Wikipedia</a>, ` +
    'licenza CC BY-SA 4.0. Visto e Mangiato non è affiliato a Michelin; la guida ufficiale è su ' +
    `<a href="${esc(GUIDE_OFFICIAL)}" rel="noopener">guide.michelin.com</a>.</p>`
  );
}

/** One restaurant of the guide as a line: name, stars, town. */
function guideItem(venue, { town = true } = {}) {
  const notes = [`<span class="stars">${starsGlyph(venue.stelle)}</span>`];
  if (town && venue.zona) notes.push(esc(venue.zona));
  const chef = tvChef(venue);
  if (chef) notes.push(`in TV: ${esc(chef)}`);
  if (venue.stato && venue.stato !== 'aperto' && venue.stato !== 'ignoto') {
    notes.push(esc(STATUS_LABELS[venue.stato] ?? '').toLowerCase());
  }
  return `<li><a href="/locale/${esc(venue.id)}">${esc(venue.nome ?? venue.id)}</a> <small>· ${notes.join(' · ')}</small></li>`;
}

/** The towns, as links to their pages, most restaurants first. */
function guideCityLinks(cities, { skip = null, limit = Infinity } = {}) {
  return [...cities.values()]
    .filter((city) => city.slug !== skip)
    .sort((a, b) => b.venues.length - a.venues.length || a.citta.localeCompare(b.citta, 'it'))
    .slice(0, limit)
    .map(
      (city) =>
        `<a class="chip" href="/stellati/${esc(city.slug)}">${esc(city.citta)} <span class="chip__n">${city.venues.length}</span></a>`
    )
    .join('');
}

/** The line under the guide's title. */
export function guideSummary(venues) {
  return `${plural(venues.length, 'ristorante', 'ristoranti')} · ${starsSummary(venues)}`;
}

/** The guide's page: by stars, then the towns. */
export function guideBody(venues) {
  const sorted = [...venues].sort(byStars);
  const parts = [];
  for (const stars of [3, 2, 1]) {
    const list = sorted.filter((venue) => venue.stelle === stars);
    if (!list.length) continue;
    parts.push(
      `<h3 class="group-label"><span class="stars">${starsGlyph(stars)}</span> ${esc(
        starsWords(stars)
      )} (${list.length})</h3><ul class="links">${list.map((v) => guideItem(v)).join('')}</ul>`
    );
  }
  parts.push(
    `<h3 class="group-label">Per città</h3><div class="chips chips--wrap">${guideCityLinks(
      guideByCity(venues)
    )}</div>`
  );
  parts.push(guideCredit());
  return parts.join('');
}

/** A town's page: its restaurants, the same town on television, the other towns. */
export function guideCityBody(city, venues, { allVenues, tvZone }) {
  const parts = [`<ul class="links">${venues.map((v) => guideItem(v, { town: false })).join('')}</ul>`];
  if (tvZone) {
    parts.push(
      `<p class="links__all"><a href="/zona/${esc(tvZone.slug)}">${esc(city)}: ${plural(
        tvZone.n,
        'ristorante visto',
        'ristoranti visti'
      )} in TV →</a></p>`
    );
  }
  parts.push(
    `<h3 class="group-label">Altre città</h3><div class="chips chips--wrap">${guideCityLinks(
      guideByCity(allVenues),
      { skip: guideCitySlug(city), limit: 30 }
    )}</div>`
  );
  parts.push(`<p class="links__all"><a href="/programma/${esc(GUIDE_ID)}">Tutti gli stellati ${GUIDE_YEAR}</a></p>`);
  parts.push(guideCredit());
  return parts.join('');
}

/** One restaurant of the guide: what the guide says, how it is today, its town. */
export function guideVenueBody(venue, { programName, sameTown }) {
  const parts = [];
  const hero = [`<h1>${esc(venue.nome ?? venue.id)}</h1>`];
  if (venue.zona) hero.push(`<p>${esc(venue.zona)}</p>`);
  hero.push(
    `<div class="card__meta"><a class="prog" href="/programma/${esc(venue.programma)}">` +
      `<span>${esc(programName)}</span></a>` +
      `<span class="tag tag--stars">${starsGlyph(venue.stelle)}</span>` +
      `<span class="state state--${esc(venue.stato)}">${esc(STATUS_LABELS[venue.stato] ?? '')}</span></div>`
  );
  parts.push(`<div class="hero">${hero.join('')}</div>`);

  const rows = [kv('Stelle', `${starsGlyph(venue.stelle)} ${starsWords(venue.stelle)}`)];
  if (venue.cucina) rows.push(kv('Cucina', venue.cucina));
  const chef = tvChef(venue);
  if (chef) rows.push(kv('Chef visto in TV', chef));
  parts.push(
    `<div class="block"><p class="block__label">Guida Michelin ${GUIDE_YEAR}</p><dl class="kv-list">${rows.join(
      ''
    )}</dl>${guideCredit()}</div>`
  );

  const today = [
    `<div class="kv"><dt>Stato</dt><dd class="state state--${esc(venue.stato)}">${esc(
      STATUS_LABELS[venue.stato] ?? ''
    )}</dd></div>`,
  ];
  const checked = italianDate(venue.verificato_il);
  if (checked) today.push(kv('Verificato', checked));
  if (venue.indirizzo) today.push(kv('Indirizzo', venue.indirizzo));
  if (venue.telefono) today.push(kv('Telefono', venue.telefono));
  parts.push(
    `<div class="block"><p class="block__label">Il locale oggi</p><dl class="kv-list kv-list--spaced">${today.join('')}</dl></div>`
  );

  if (venue.zona) {
    const list = sameTown.length
      ? `<ul class="links">${sameTown.map((v) => guideItem(v, { town: false })).join('')}</ul>`
      : '';
    parts.push(
      `<div class="block block--last"><h2 class="block__label">Stellati · ${esc(venue.zona)}</h2>${list}` +
        `<p class="links__all"><a href="/stellati/${esc(guideCitySlug(venue.zona))}">${esc(
          guideCityTitle(venue.zona)
        )}</a></p></div>`
    );
  } else {
    parts[parts.length - 1] = parts[parts.length - 1].replace('<div class="block">', '<div class="block block--last">');
  }
  return parts.join('');
}

/* ---------- Cucine da Incubo, chiusi e aperti ---------- */

/**
 * The page at `STATUS_PATH`: the same groups and words the app draws there
 * (`openStatusPage` in `src/main.js`), each venue as a link to its card.
 */
export function statusBody(groups, total) {
  const item = (venue) => {
    const notes = [];
    if (venue.zona) notes.push(esc(venue.zona));
    if (venue.puntata?.data) notes.push(`in TV ${esc(venue.puntata.data.slice(0, 4))}`);
    const checked = venue.stato !== 'ignoto' && italianDate(venue.verificato_il);
    const label = STATUS_LABELS[venue.stato];
    if (label && venue.stato !== 'ignoto') {
      notes.push(esc(checked ? `${label}, verificato il ${checked}` : label));
    }
    return `<li><a href="/locale/${esc(venue.id)}">${esc(venue.nome ?? venue.id)}</a> <small>· ${notes.join(' · ')}</small></li>`;
  };
  return (
    `<p class="status-intro">${esc(statusIntro(total))}</p>` +
    groups
      .map(
        (group) =>
          `<h3 class="group-label">${esc(group.label)} (${group.venues.length})</h3>` +
          (group.note ? `<p class="status-intro">${esc(group.note)}</p>` : '') +
          `<ul class="links">${group.venues.map(item).join('')}</ul>`
      )
      .join('')
  );
}
